import type { Directive } from 'vue';

/**
 * v-tap 指令
 * 解决微信内置浏览器（H5）中 click 事件间歇性不触发的问题。
 *
 * 原理：微信 WebView 的 Native 手势层会间歇性吞噬 touch 事件，导致 click 不产生。
 * touchstart/touchend 在 Native 手势判定之前触发，所以更可靠。
 * 通过判断手指移动距离和按压时长来区分"点击"和"滚动"。
 *
 * 用法：<view v-tap="handleClick">点击我</view>
 * 支持修饰符：<view v-tap.stop="handleClick">阻止冒泡</view>
 */

const TAP_MAX_DISTANCE = 10; // 手指最大移动距离（px）
const TAP_MAX_DURATION = 350; // 最大按压时长（ms）

interface TapHTMLElement extends HTMLElement {
  _tapCleanup?: () => void;
  _tapHandled?: number;
}

const TAP_DEDUP_WINDOW = 500; // 去重时间窗口（ms）

const vTap: Directive<TapHTMLElement, Function> = {
  mounted(el, binding) {
    // #ifdef H5
    if (typeof window === 'undefined' || !('ontouchstart' in window)) return;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let isScrolling = false;
    let tapFired = false;

    const fireTap = (e: TouchEvent) => {
      if (tapFired) return; // 防止 touchend 和 touchcancel 都触发时重复执行
      tapFired = true;

      // 标记已处理，用于去重
      el._tapHandled = Date.now();

      if (binding.modifiers.stop) {
        e.stopPropagation();
      }

      // 阻止浏览器产生真实 click，避免重复触发
      e.preventDefault();

      // 派发合成 click 事件，保留 DOM 冒泡能力（uv-tabbar 等组件依赖冒泡）
      const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
      (clickEvent as any)._fromTap = true;
      el.dispatchEvent(clickEvent);
    };

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
      isScrolling = false;
      tapFired = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (isScrolling) return;
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - startX);
      const dy = Math.abs(touch.clientY - startY);
      if (dx > TAP_MAX_DISTANCE || dy > TAP_MAX_DISTANCE) {
        isScrolling = true;
      }
    };

    // touchend：正常触摸结束
    const onTouchEnd = (e: TouchEvent) => {
      const dt = Date.now() - startTime;
      if (!isScrolling && dt < TAP_MAX_DURATION) {
        fireTap(e);
      }
    };

    // touchcancel：微信 Native 手势层吞噬了触摸事件时触发
    // 如果手指没有移动且时间短，仍然当作点击处理
    const onTouchCancel = (e: TouchEvent) => {
      const dt = Date.now() - startTime;
      if (!isScrolling && dt < TAP_MAX_DURATION) {
        fireTap(e);
      }
    };

    // 在 capture 阶段拦截 click
    const onClickCapture = (e: MouseEvent) => {
      // 合成 click（v-tap 派发的）放行
      if ((e as any)._fromTap) return;

      // 真实 click 且 v-tap 已经处理过 → 拦截，防止重复（兜底，preventDefault 已阻止大部分）
      if (el._tapHandled && Date.now() - el._tapHandled < TAP_DEDUP_WINDOW) {
        e.stopImmediatePropagation();
        e.stopPropagation();
        e.preventDefault();
        el._tapHandled = undefined;
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchCancel);
    el.addEventListener('click', onClickCapture, true); // true = capture 阶段

    // 保存清理函数
    el._tapCleanup = () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
      el.removeEventListener('click', onClickCapture, true);
    };
    // #endif
  },

  unmounted(el) {
    // #ifdef H5
    el._tapCleanup?.();
    el._tapCleanup = undefined;
    // #endif
  },
};

export default vTap;

